<script setup lang="ts">
import { CirclePlus, Loader2 } from 'lucide-vue-next'
import { useVModel } from '@vueuse/core'

import axios from 'axios'

import CardTitle from '@/components/CardTitle.vue'
import CustomerRewardsModal from '@/views/customer/Components/CustomerRewardsModal.vue'

import useNotification from '@/composables/notification'

import type { LoyaltyReward } from '@/types/global'
import type { CustomerDetails } from '@/types/types'

const props = defineProps<{
  pointsToSet: number | string | null
  currencyToSet: number | string | null
  customer: CustomerDetails
  isRewardModalOpen: boolean
  isPointsFormModalOpen: boolean
  isPointsToSetProcessing: boolean
  rewards: LoyaltyReward[] | null
  displayMobileLoyalty: boolean
  rewardIsProcessing: {
    modal: boolean
  }
}>()

const { formatNumber } = useNumber()
const { stores, globalConfig, isSupportConnection } = useLocalStorage()
const { t } = useI18n()
const hasPointsByCurrency = computed(() => globalConfig.value.merchant.hasPointsByCurrency)
const notification = useNotification()

const selectedReasonId = ref(1)
const selectedStoreId = ref(props.customer.referenceStoreId || 0)

const emit = defineEmits<{
  (e: 'modelValue'): void
  (e: 'onSetPoints', reasonSlug: string, storeId: number): void
  (e: 'onOpenRewardModal'): void
  (e: 'onOpenPointsModal'): void
  (e: 'onOpenPointsFormModal'): void
  (e: 'onClosePointsFormModal'): void
  (e: 'onCloseRewardModal'): void
  (e: 'onClaimReward'): void
}>()

const pointsModel = useVModel(props, 'pointsToSet', emit)
const currencyModel = useVModel(props, 'currencyToSet', emit)

const inputLabel = computed(() => {
  return selectedReasonId.value === 3 ? '' : t('pages.customer.customerDetailPage.blocks.points.form.points')
})

const handleSubmit = () => {
  const reason = formattedReason.value.find((reason) => reason.id === selectedReasonId.value).slug
  emit('onSetPoints', reason, selectedStoreId.value)
}

const coin = computed(() => `${import.meta.env.VITE_AWS_STORAGE_URL}/b2c/webapp/coin.png`)

const handleStoreSelection = async (id: number) => {
  selectedStoreId.value = id
}
const handleReasonSelection = (reasonId: number) => {
  selectedReasonId.value = reasonId
}

const formattedReason = computed(() => {
  const reasons = [
    {
      id: 1,
      slug: 'checkin',
      text: t('pages.customer.customerDetailPage.blocks.points.form.checkin')
    },
    {
      id: 2,
      slug: 'manual',
      text: t('pages.customer.customerDetailPage.blocks.points.form.manual')
    }
  ]

  if (hasPointsByCurrency.value) {
    reasons.push({
      id: 3,
      slug: 'order',
      text: t('pages.customer.customerDetailPage.blocks.points.form.order')
    })
  }

  return reasons
})

const formattedStores = computed(() => {
  return stores.value.map((store) => {
    return {
      id: store.id,
      text: store.name
    }
  })
})

const setReasonAsManual = () => {
  const hasValidPointsByCurrency =
    (hasPointsByCurrency.value && Number(pointsModel.value) >= 0) || Number(pointsModel.value) >= 0
  if (hasValidPointsByCurrency) {
    return
  }
  selectedReasonId.value = 2
}

const pointsByCurrency = computed(() => {
  const baseCurrency = Number(currencyModel.value)

  const loyaltyPointsByCurrencyUnit = Number(globalConfig.value.merchant.loyaltyPointsByCurrencyUnit) || 1

  return Math.floor(baseCurrency * loyaltyPointsByCurrencyUnit)
})

const isSubmitButtonDisabled = computed(() => {
  return selectedReasonId.value === 3
    ? !currencyModel.value || Number(currencyModel.value) === 0
    : !pointsModel.value || Number(pointsModel.value) === 0
})

const setPointsByCurrencyToModel = () => {
  if (hasPointsByCurrency.value) {
    pointsModel.value = pointsByCurrency.value
  }
}

const isRecalculatingPoints = ref(false)

const handleRecalculatePoints = async () => {
  isRecalculatingPoints.value = true
  try {
    await axios.patch(`/customers/${props.customer.id}/points/recalculate`)
    notification.create('success', {
      text: t('pages.customer.customerDetailPage.blocks.points.recalculate.success'),
      timeout: 2000
    })
    emit('onClaimReward')
  } catch {
    notification.create('error', {
      text: t('pages.customer.customerDetailPage.blocks.points.recalculate.error'),
      timeout: 2000
    })
  } finally {
    isRecalculatingPoints.value = false
  }
}

onMounted(() => {
  if (hasPointsByCurrency.value) {
    selectedReasonId.value = 3
  } else {
    pointsModel.value = globalConfig.value.merchant.checkinPoints
  }
})

watch(
  () => pointsModel.value,
  () => setReasonAsManual()
)

watch(
  () => currencyModel.value,
  () => setPointsByCurrencyToModel()
)
</script>

<template>
  <div v-show="displayMobileLoyalty" class="grid grid-cols-12">
    <div class="col-span-12 space-y-6">
      <div class="rounded-lg border bg-white">
        <div class="mx-6 flex flex-col justify-between pb-4 pt-4 sm:py-5 lg:flex-row lg:items-center">
          <div class="flex items-center justify-between sm:mb-5 lg:mb-0">
            <CardTitle
              :title="t('pages.customer.customerDetailPage.blocks.points.title')"
              :subtitle="t('pages.customer.customerDetailPage.blocks.points.description')"
              :display-subtitle-on-mobile="false"
              name="HeroGiftIconOutline"
            />
            <div class="ml-4 rounded-md bg-primary/5 px-4 py-2 text-primary lg:hidden">
              <div class="flex w-full items-center gap-1 sm:items-baseline">
                <span class="text-2xl font-bold lg:text-3xl">{{ formatNumber(customer.points) }}</span>
                <span class="text-sm">
                  {{ t('pages.customer.customerDetailPage.blocks.points.points') }}
                </span>
              </div>
            </div>
          </div>

          <div class="flex flex-col items-center justify-center gap-4 lg:flex-row lg:gap-0">
            <div
              class="fixed bottom-0 left-0 z-10 block w-full items-center justify-center border-t bg-white px-6 py-5 sm:relative sm:w-auto sm:border-0 sm:bg-transparent"
            >
              <Button variant="outline-primary" :disabled="rewardIsProcessing.modal" @click="emit('onOpenRewardModal')">
                <Loader2 v-if="rewardIsProcessing.modal" class="animate-spin" />
                <CirclePlus v-else class="hidden sm:block" />
                {{ t('pages.customer.customerDetailPage.reward.button') }}
              </Button>
            </div>
            <div class="ml-4 hidden rounded-md bg-primary/5 px-4 py-2 text-primary lg:block">
              <div class="flex w-full items-baseline gap-1">
                <span class="font-bold lg:text-3xl">{{ formatNumber(customer.points) }}</span>
                <span class="text-sm">
                  {{ t('pages.customer.customerDetailPage.blocks.points.points') }}
                </span>
                <img
                  v-if="isSupportConnection"
                  :src="coin"
                  :class="[
                    'block h-auto w-5 cursor-pointer',
                    { 'opacity-50 pointer-events-none': isRecalculatingPoints }
                  ]"
                  @click="handleRecalculatePoints"
                />
                <img v-else :src="coin" class="block h-auto w-5" />
              </div>
            </div>
          </div>
        </div>
        <div class="hidden border-t px-4 py-5 sm:block sm:px-6">
          <div class="rounded-md bg-muted p-5">
            <div class="mb-5 flex items-center text-sm text-foreground">
              <BaseIcon name="HeroArrowsRightLeftIconSolid" class="mr-2 h-5 w-5" />
              <h3>{{ t('pages.customer.customerDetailPage.blocks.points.form.title') }}</h3>
            </div>
            <form @submit.prevent="handleSubmit">
              <div class="grid w-full grid-cols-11 items-end gap-6 md:grid-cols-12 lg:grid-cols-11">
                <div
                  v-if="hasPointsByCurrency && selectedReasonId === 3"
                  class="col-span-11 md:col-span-6 lg:col-span-3"
                >
                  <div class="mb-2 flex items-center gap-1">
                    <p class="text-sm font-medium text-foreground">
                      {{ t('pages.customer.customerDetailPage.blocks.points.form.amount') }}
                    </p>
                    <AppTooltip tooltip-position="right">
                      <BaseIcon
                        name="HeroExclamationCircleIconSolid"
                        class="mr-2 h-5 w-5 rotate-180 transform cursor-pointer"
                      />
                      <template #tooltip-content>
                        <div class="m-2 flex flex-col gap-3">
                          <p class="w-28 text-left text-sm lg:w-96">
                            {{ t('pages.customer.customerDetailPage.blocks.points.tooltip') }}
                          </p>
                        </div>
                      </template>
                    </AppTooltip>
                  </div>
                  <AppFormInput
                    id="points"
                    v-model="currencyModel"
                    :autofocus="true"
                    type="number"
                    name="points"
                    placeholder="0"
                    :class="currencyModel === 0 && 'text-muted-foreground'"
                    @wheel.prevent
                  >
                    <template #icon-right>
                      <span class="text-sm text-muted-foreground">
                        {{ t('pages.customer.customerDetailPage.blocks.points.euro') }}
                      </span>
                    </template>
                  </AppFormInput>
                </div>
                <div v-else class="col-span-11 md:col-span-6 lg:col-span-3">
                  <AppFormInput
                    id="points"
                    v-model="pointsModel"
                    :autofocus="true"
                    type="number"
                    inputmode="numeric"
                    name="points"
                    placeholder="0"
                    :label="inputLabel"
                    :class="pointsModel == 0 && 'text-muted-foreground'"
                    @wheel.prevent
                  >
                    <template #icon-right>
                      <span class="text-sm text-muted-foreground">
                        {{ t('pages.customer.customerDetailPage.blocks.points.points') }}
                      </span>
                    </template>
                  </AppFormInput>
                </div>
                <div class="col-span-11 md:col-span-6 lg:col-span-3">
                  <label class="mb-1 block text-sm font-medium text-foreground">
                    {{ t('pages.customer.customerDetailPage.blocks.points.form.reason') }}
                  </label>
                  <ComboboxSelect
                    value-key="id"
                    label-key="text"
                    :placeholder="t('pages.customer.customerDetailPage.blocks.points.form.reason')"
                    :model-value="selectedReasonId"
                    :options="formattedReason"
                    trigger-class="w-full"
                    :disabled="Number(pointsModel) < 0"
                    @update:model-value="handleReasonSelection"
                  />
                </div>
                <div class="col-span-11 md:col-span-6 lg:col-span-3">
                  <label class="mb-1 block text-sm font-medium text-foreground">
                    {{ t('pages.customer.customerDetailPage.blocks.points.form.store') }}
                  </label>
                  <ComboboxSelect
                    value-key="id"
                    label-key="text"
                    :placeholder="t('pages.customer.customerPage.blocks.create.fields.chooseStore')"
                    :model-value="selectedStoreId"
                    :options="formattedStores"
                    trigger-class="w-full"
                    :disabled="Number(pointsModel) < 0"
                    @update:model-value="handleStoreSelection"
                  />
                </div>
                <div class="col-span-11 md:col-span-6 lg:col-span-2">
                  <Button
                    type="button"
                    variant="default-primary"
                    :disabled="isSubmitButtonDisabled"
                    class="h-10 w-full"
                    @click="handleSubmit"
                  >
                    {{ t('pages.customer.customerDetailPage.blocks.points.form.submit') }}
                  </Button>
                </div>
              </div>
            </form>
            <div v-if="hasPointsByCurrency && selectedReasonId === 3">
              <p v-if="!currencyModel" class="mt-3 text-left text-xs text-muted-foreground">
                {{ t('pages.customer.customerDetailPage.blocks.points.form.subtitleCurrency') }}
              </p>
              <p v-else class="mt-3 text-left text-xs text-muted-foreground">
                {{ t('pages.customer.customerDetailPage.blocks.points.form.subtitleAttributeCurrency') }}
                <span class="font-bold">
                  {{
                    t('pages.customer.customerDetailPage.blocks.points.form.subtitleCurrencyPoints', {
                      currency: pointsByCurrency
                    })
                  }}
                </span>
              </p>
            </div>
            <div v-else>
              <p class="mt-3 text-xs text-muted-foreground">
                {{ t('pages.customer.customerDetailPage.blocks.points.form.subtitle') }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div>
    <div class="fixed bottom-0 left-0 z-20 w-full items-center justify-center border-t bg-white px-6 py-5 sm:hidden">
      <Button variant="default-primary" class="w-full py-3" @click="emit('onOpenPointsFormModal')">
        {{ t('pages.customer.customerDetailPage.blocks.points.form.submit') }}
      </Button>
      <Button
        variant="outline-primary"
        :disabled="rewardIsProcessing.modal"
        class="mt-3 w-full py-3"
        @click="emit('onOpenRewardModal')"
      >
        <Loader2 v-if="rewardIsProcessing.modal" class="animate-spin" />
        <CirclePlus v-else class="hidden sm:block" />
        {{ t('pages.customer.customerDetailPage.reward.button') }}
      </Button>
    </div>
  </div>
  <CustomerRewardsModal
    :is-open="isRewardModalOpen"
    :rewards="rewards"
    :customer="customer"
    @on-claim-reward="emit('onClaimReward')"
    @close-customer-rewards-modal="emit('onCloseRewardModal')"
  />

  <BaseModal
    :is-open="isPointsFormModalOpen"
    :heading-title="t('pages.customer.customerDetailPage.blocks.points.modal.adjust')"
    :link-text="t('pages.customer.customerDetailPage.blocks.points.modal.adjust')"
    :is-processing="isPointsToSetProcessing"
    :is-disabled="pointsModel === null || pointsModel == 0"
    @click="handleSubmit"
    @close="emit('onClosePointsFormModal')"
  >
    <form @submit.prevent="handleSubmit">
      <div class="mt-14 grid w-full grid-cols-11 items-end gap-6 rounded-md bg-muted p-3">
        <div v-if="hasPointsByCurrency && selectedReasonId === 3" class="col-span-11">
          <div class="mb-2 flex items-center gap-1">
            <p>{{ t('pages.customer.customerDetailPage.blocks.points.form.amount') }}</p>
            <AppTooltip tooltip-position="right">
              <BaseIcon
                name="HeroExclamationCircleIconSolid"
                class="mr-2 h-5 w-5 rotate-180 transform cursor-pointer"
              />
              <template #tooltip-content>
                <div class="m-2 flex flex-col gap-3">
                  <p class="w-28 text-left text-sm lg:w-96">
                    {{ t('pages.customer.customerDetailPage.blocks.points.tooltip') }}
                  </p>
                </div>
              </template>
            </AppTooltip>
          </div>
          <AppFormInput
            id="points"
            v-model="currencyModel"
            :autofocus="true"
            type="number"
            name="points"
            placeholder="0"
            :class="currencyModel === 0 && 'text-muted-foreground'"
            @wheel.prevent
          >
            <template #icon-right>
              <span class="text-sm text-muted-foreground">
                {{ t('pages.customer.customerDetailPage.blocks.points.euro') }}
              </span>
            </template>
          </AppFormInput>
          <p v-if="!currencyModel" class="mt-3 text-left text-xs text-muted-foreground">
            {{ t('pages.customer.customerDetailPage.blocks.points.form.subtitleCurrency') }}
          </p>
          <p v-else class="mt-3 text-left text-xs text-muted-foreground">
            {{ t('pages.customer.customerDetailPage.blocks.points.form.subtitleAttributeCurrency') }}
            <span class="font-bold">
              {{
                t('pages.customer.customerDetailPage.blocks.points.form.subtitleCurrencyPoints', {
                  currency: pointsByCurrency
                })
              }}
            </span>
          </p>
        </div>
        <div v-else class="col-span-11">
          <AppFormInput
            id="points"
            v-model="pointsModel"
            :autofocus="true"
            type="number"
            name="points"
            placeholder="0"
            :label="inputLabel"
            :class="pointsModel == 0 && 'text-muted-foreground'"
            @wheel.prevent
          >
            <template #icon-right>
              <span class="text-sm text-muted-foreground">
                {{ t('pages.customer.customerDetailPage.blocks.points.points') }}
              </span>
            </template>
          </AppFormInput>
          <p class="mt-3 text-xs text-muted-foreground">
            {{ t('pages.customer.customerDetailPage.blocks.points.form.subtitle') }}
          </p>
        </div>
        <div class="col-span-11">
          <label class="mb-1 block text-sm font-medium text-foreground">
            {{ t('pages.customer.customerDetailPage.blocks.points.form.reason') }}
          </label>
          <ComboboxSelect
            value-key="id"
            label-key="text"
            :placeholder="t('pages.customer.customerDetailPage.blocks.points.form.reason')"
            :model-value="selectedReasonId"
            :options="formattedReason"
            trigger-class="w-full"
            :disabled="Number(pointsModel) < 0"
            @update:model-value="handleReasonSelection"
          />
        </div>
        <div class="col-span-11">
          <label class="mb-1 block text-sm font-medium text-foreground">
            {{ t('pages.customer.customerDetailPage.blocks.points.form.store') }}
          </label>
          <ComboboxSelect
            value-key="id"
            label-key="text"
            :placeholder="t('pages.customer.customerPage.blocks.create.fields.chooseStore')"
            :model-value="selectedStoreId"
            :options="formattedStores"
            trigger-class="w-full"
            :disabled="Number(pointsModel) < 0"
            @update:model-value="handleStoreSelection"
          />
        </div>
      </div>
    </form>
  </BaseModal>
</template>
